import { useCallback, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { fetchWorkspace, getAdminUser, refreshAdminSession } from "../api/adminClient";
import { mergeWorkspaceApiIntoServerIdentity } from "../ai/workspaceContext";
import { cacheWorkspacePdfBranding } from "../utils/workspacePdfBranding";
import { buildWorkspaceBrandingContext } from "./assistantPageContext";
import TenantOnboardingAssistant from "./TenantOnboardingAssistant";
import WorkspaceBrandingCard from "./WorkspaceBrandingCard";
import WorkspaceProductSettingsPanel from "./WorkspaceProductSettingsPanel";

export default function WorkspaceBrandingPage() {
  const { setAdminAssistantContext } = useOutletContext() || {};

  const refreshAssistantContext = useCallback(async () => {
    if (!setAdminAssistantContext) return;
    try {
      const res = await fetchWorkspace();
      const ws = res.data?.workspace;
      setAdminAssistantContext(buildWorkspaceBrandingContext({ workspace: ws }));
    } catch {
      setAdminAssistantContext(buildWorkspaceBrandingContext());
    }
  }, [setAdminAssistantContext]);

  useEffect(() => {
    refreshAssistantContext();
    return () => setAdminAssistantContext?.(null);
  }, [refreshAssistantContext, setAdminAssistantContext]);

  const handleOnboardingSaved = useCallback(async () => {
    await refreshAdminSession();
    try {
      const res = await fetchWorkspace();
      const ws = res.data?.workspace;
      const user = getAdminUser();
      if (ws && user?.tenant_id) {
        cacheWorkspacePdfBranding({
          tenant_id: user.tenant_id,
          product_display_name: ws.product_display_name,
          logo_url: ws.logo_url,
          primary_color: ws.primary_color,
          email_pack: ws.email_pack,
        });
      }
    } catch {
      /* non-fatal */
    }
    await refreshAssistantContext();
  }, [refreshAssistantContext]);

  return (
    <div className="w-full px-3 sm:px-5 lg:px-6 py-5 sm:py-6 box-border">
      <TenantOnboardingAssistant onSaved={handleOnboardingSaved} />
      <div className="mb-6">
        <WorkspaceProductSettingsPanel
          onSaved={async () => {
            try {
              const res = await fetchWorkspace();
              const ws = res.data?.workspace;
              const user = getAdminUser();
              if (ws && user?.tenant_id) {
                mergeWorkspaceApiIntoServerIdentity(ws, user);
                cacheWorkspacePdfBranding({
                  tenant_id: user.tenant_id,
                  product_display_name: ws.product_display_name,
                  logo_url: ws.logo_url,
                  primary_color: ws.primary_color,
                  email_pack: ws.email_pack,
                });
              }
              await refreshAssistantContext();
            } catch {
              /* non-fatal */
            }
          }}
        />
      </div>
      <WorkspaceBrandingCard
        onSaved={async () => {
          await refreshAdminSession();
          try {
            const res = await fetchWorkspace();
            const ws = res.data?.workspace;
            const user = getAdminUser();
            if (ws && user?.tenant_id) {
              mergeWorkspaceApiIntoServerIdentity(ws, user);
              cacheWorkspacePdfBranding({
                tenant_id: user.tenant_id,
                product_display_name: ws.product_display_name,
                logo_url: ws.logo_url,
                primary_color: ws.primary_color,
                email_pack: ws.email_pack,
              });
            }
          } catch {
            /* non-fatal */
          }
          await refreshAssistantContext();
        }}
      />
    </div>
  );
}
